/**
 * ══════════════════════════════════════════════════════════════
 * RecommendationPanel — Componente do Design System
 * ══════════════════════════════════════════════════════════════
 *
 * Exibe recomendações operacionais derivadas de insights e
 * diagnósticos, com indicação de prioridade, impacto estimado,
 * esforço e responsável sugerido.
 *
 * Uso: import { RecommendationPanel } from '@/components/system';
 * ══════════════════════════════════════════════════════════════
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, ArrowRight, Target, User, Gauge,
  ChevronRight, TrendingDown,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import type { OperationalRecommendation } from '@/types/recommendations';
import { PRIORITY_LABELS, CATEGORY_LABELS, EFFORT_LABELS } from '@/types/recommendations';

// ─── Style maps ─────────────────────────────────────────────

const PRIORITY_STYLES: Record<string, { badge: string; accent: string }> = {
  critical: {
    badge: 'bg-destructive/10 text-destructive border-destructive/30',
    accent: 'border-l-destructive',
  },
  high: {
    badge: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    accent: 'border-l-orange-500',
  },
  medium: {
    badge: 'bg-primary/10 text-primary border-primary/30',
    accent: 'border-l-primary',
  },
  low: {
    badge: 'bg-muted text-muted-foreground border-border',
    accent: 'border-l-muted-foreground/30',
  },
};

const EFFORT_ICONS: Record<string, string> = {
  low: '●○○',
  medium: '●●○',
  high: '●●●',
};

// ─── Props ──────────────────────────────────────────────────

export interface RecommendationPanelProps {
  recommendations: OperationalRecommendation[];
  /** Called when panel is dismissed */
  onClose?: () => void;
  className?: string;
}

// ─── Component ──────────────────────────────────────────────

export function RecommendationPanel({ recommendations, onClose, className = '' }: RecommendationPanelProps) {
  const navigate = useNavigate();

  if (recommendations.length === 0) return null;

  return (
    <div className={`rounded-[10px] border bg-card overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 border-b bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Recomendações de Ação</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {recommendations.length} ação(ões) sugerida(s) com base no diagnóstico
              </p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xs">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Recommendations list */}
      <div className="p-4 space-y-3">
        {recommendations.map((rec) => (
          <RecommendationItem key={rec.id} rec={rec} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}

// ─── Recommendation Item ────────────────────────────────────

function RecommendationItem({
  rec,
  navigate,
}: {
  rec: OperationalRecommendation;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const style = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.medium;

  return (
    <div
      role={rec.drillDownRoute ? 'button' : undefined}
      onClick={() => rec.drillDownRoute && navigate(rec.drillDownRoute)}
      className={`rounded-lg border border-l-4 bg-background p-4 transition-all space-y-3
        ${style.accent}
        ${rec.drillDownRoute ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}`}
    >
      {/* Title row */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="outline" className={`text-xs ${style.badge}`}>
              {PRIORITY_LABELS[rec.priority]}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {CATEGORY_LABELS[rec.category]}
            </Badge>
          </div>
          <h4 className="text-sm font-semibold leading-tight">{rec.title}</h4>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{rec.description}</p>
        </div>
        {rec.drillDownRoute && (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 mt-1" />
        )}
      </div>

      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        {/* Suggested owner */}
        <span className="flex items-center gap-1">
          <User className="h-3 w-3" />
          {rec.suggestedOwner}
        </span>

        {/* Effort */}
        <span className="flex items-center gap-1">
          <Gauge className="h-3 w-3" />
          Esforço: {EFFORT_LABELS[rec.effortLevel]}
          <span className="font-mono text-[8px] tracking-widest">{EFFORT_ICONS[rec.effortLevel]}</span>
        </span>
      </div>

      {/* Impact estimate */}
      {rec.estimatedImpact && (
        <div className="bg-muted/30 rounded-md p-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <TrendingDown className="h-3 w-3 text-success" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Impacto Estimado
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">{rec.estimatedImpact.description}</p>
          {(rec.estimatedImpact.scoreReduction != null || rec.estimatedImpact.mitigationPct != null) && (
            <div className="flex gap-4 pt-1">
              {rec.estimatedImpact.scoreReduction != null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Score</span>
                  <span className="text-xs font-semibold text-success">-{rec.estimatedImpact.scoreReduction}pts</span>
                </div>
              )}
              {rec.estimatedImpact.mitigationPct != null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Mitigação</span>
                  <Progress value={rec.estimatedImpact.mitigationPct} className="w-16 h-1.5" />
                  <span className="text-xs font-semibold tabular-nums">{rec.estimatedImpact.mitigationPct}%</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Target entities */}
      {rec.targetEntities.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {rec.targetEntities.map((t) => (
            <button
              key={t.id}
              onClick={(e) => {
                if (t.drillDownRoute) {
                  e.stopPropagation();
                  navigate(t.drillDownRoute);
                }
              }}
              disabled={!t.drillDownRoute}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium transition-colors
                bg-muted/50 hover:bg-muted
                ${t.drillDownRoute ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <Target className="h-2.5 w-2.5" />
              {t.name}
              {t.drillDownRoute && <ArrowRight className="h-2 w-2" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default RecommendationPanel;
